<template>
  <div class="mt-4">
    <PageContainer v-if="flowerGenus && deliveryDates">
      <div class="breadcrumb-list d-flex align-center">
        <v-icon color="#00796B">mdi-home</v-icon>
        <router-link
          class="link ml-2"
          style="color: #00796B"
          to="/shop/products"
        >{{ $t("shopProducts") }}</router-link>
        <div class="arrow">/</div>
        <div style="color: #00796B">{{ flowerGenus.name }}</div>
        <div class="arrow">/</div>
        <strong>{{ flowerSpecie.name }}</strong>
      </div>

      <div class="d-md-flex">
        <ProductImage class="product-image flex-shrink-0" border :src="productImage" />
        <div class="d-flex flex-column" style="width: 100%;">
          <ProductDetailCard
            class="flex-grow-1"
            v-bind="{ flower, deliveryDates }"
          />
          <ProductFarmDetailCard
            v-bind="{ farm }"
            :goToConversation="goToConversation"
          />
        </div>
      </div>
    </PageContainer>

    <PageContainer pc-only>
      <div class="order-form" v-if="deliveryDates">
        <ProductFormGroup :label='$t("delivery_date")' required>
          <v-select
            v-model="deliveryDate"
            item-text="label"
            :items="deliveryDatesOptions"
            :label="$t('please_select')"
            :style="!mobile && 'width: 200px'"
            :full-width="mobile"
            solo
          />
        </ProductFormGroup>

        <ProductFormGroup :label="$t('standard')" v-if="deliveryDate" required>
          <ProductHarvests v-model="harvestId" :flowerHarvests="flowerHarvests" />
        </ProductFormGroup>

        <ProductFormGroup :label="$t('normal_order_count')" v-if="harvestId && !flower.request_only" full-width>
          <ProductColorAndCount v-model="flowerCount" :harvest="harvest" includesZero />
        </ProductFormGroup>

        <simple-dialog class="mb-3 mt-5 ga-shop-products-detail-new_feature" v-if="harvestId" btnColor="#ff8C71">
          <template v-slot:contents>
            <p class="text-h6 font-weight-bold text-center">リクエスト機能とは？</p>
            <p class="text-body-1">通常取引分が売り切れ（0となっている）場合に、生産者さんに追加リクエストできる機能です。</p>
            <p class="text-body-1">花屋さんの追加リクエストに対し、生産者さんが承認した場合、購入となり実際に納品させて頂きます。</p>
          </template>

          <template v-slot:btn-contents>
            <v-icon color="white">mdi-help-circle</v-icon>
            <span class="white--text font-weight-bold">もっと欲しい人向け お試し機能</span>
          </template>
        </simple-dialog>

        <ProductFormGroup :label="$t('request_count')" v-if="harvestId" full-width :required="flowerSpecie.request_only">
          <ProductColorAndCount v-model="requestFlowerCount" :harvest="harvest" :includesZero="!flowerSpecie.request_only" :count="10" />
        </ProductFormGroup>

        <div class="cart d-flex justify-center flex-column" v-if="flowerCount || requestFlowerCount">
          <Button class="size-big ga-shop-products-detail-add_cart" :on-click="addDraftOrder">{{ $t("add_to_cart") }}</Button>
          <simple-dialog class="mb-3 mt-5 ga-shop-products-detail-new_feature" v-if="harvestId" btnColor="#2c7eb1" :block="true" :xLarge="true">
          <template v-slot:contents>
            <p class="text-h6 font-weight-bold text-center">購入・リクエスト内容の確認</p>
            <p class="text-body-2">{{ $t("delivery_date") }}: {{ deliveryDate }}</p>
            <p class="text-body-2">生産者: {{ flower.farm.name }}</p>
            <p class="text-body-2">品目: {{ flower.flower_specie.flower_genus.name }}</p>
            <p class="text-body-2">品種: {{ flower.flower_specie.name }}</p>
            <v-divider class="mb-2" />
            <p class="text-body-2">購入: {{ flowerCount }}本</p>
            <p class="text-body-2">リクエスト: {{ requestFlowerCount }}本</p>
            <div class="d-flex justify-center">
              <Button class="size-big ga-shop-products-detail-purchace_now" color="#2c7eb1" :on-click="purchace">{{ $t("purchace_now") }}</Button>
            </div>
          </template>

          <template v-slot:btn-contents :width="100">
            <div class="white--text text-h6">{{ $t("purchace_now_after_confirm") }}</div>
          </template>
        </simple-dialog>
        </div>
      </div>
    </PageContainer>
  </div>
</template>

<script>
import PageContainer from "@/components/shop/PageContainer.vue";
import Button from "@/components/shop/LoadingButton.vue";
import ProductImage from "@/components/shop/products/ProductImage.vue";
import ProductDetailCard from "@/components/shop/products/ProductDetailCard.vue";
import ProductFarmDetailCard from "@/components/shop/products/ProductFarmDetailCard.vue";
import ProductFormGroup from "@/components/shop/products/ProductFormGroup.vue";
import ProductHarvests from "@/components/shop/products/ProductHarvests2.vue";
import ProductColorAndCount from "@/components/shop/products/ProductColorAndCount.vue";
import SimpleDialog from "@/components/shared/SimpleDialog.vue";

export default {
  components: {
    PageContainer,
    Button,
    ProductImage,
    ProductDetailCard,
    ProductFarmDetailCard,
    ProductFormGroup,
    ProductHarvests,
    ProductColorAndCount,
    SimpleDialog
  },

  data() {
    return {
      productId: +this.$route.params.productId,
      deliveryDate: "",
      harvestId: null,
      flower: null,
      farm: null,
      flowerGenus: null,
      flowerSpecie: null,
      flowerSpecieId: 0,
      flowerCount: 0,
      requestFlowerCount: 0,
      deliveryDates: null,
      deliveryDatesOptions: [],
      flowerHarvests: [],
      deliveryDateHarvestIds: []
    };
  },

  watch: {
    deliveryDate() {
      this.getHarvest();
    },
    harvestId() {
      this.flowerCount = 0;
      this.requestFlowerCount = 0;
    }
  },

  async mounted() {
    const { result, status } = await this.$http(
      "get",
      `/harvests/flowers/${this.$route.params.productId}`
    );
    if (status === 200 && result.delivery_date_harvest_ids.length > 0) {
      this.flowerId = Math.floor(this.$route.params.productId);
      this.flower = result.flower;
      this.farm = result.flower.farm;
      this.flowerGenus = result.flower.flower_specie.flower_genus;
      this.flowerSpecie = result.flower.flower_specie;
      this.deliveryDateHarvestIds = result.delivery_date_harvest_ids;
      this.deliveryDates = [...new Set(result.delivery_date_harvest_ids.map(delivery_date_harvest_id => delivery_date_harvest_id.delivery_date))];
      this.deliveryDatesOptions = this.deliveryDates.map(value => ({
        value,
        label: this.$d(value, "date")
      }));
    }
    if (status === 512 || result.delivery_date_harvest_ids.length === 0) {
      this.$store.dispatch("setSnackBar", { msg: "この商品は現在、出品されていません。", color: "red" }, { root: true });
      this.$store.dispatch("snackOn", {}, { root: true });
    }
    if (status === 404) {
      this.$store.dispatch("setSnackBar", { msg: "この商品は存在しません。", color: "red" }, { root: true });
      this.$store.dispatch("snackOn", {}, { root: true });
    }
  },

  methods: {
    async getHarvest() {
      this.flowerHarvests = [];
      const promises = [];
      this.deliveryDateHarvestIds.forEach(deliveryDateHarvestId => {
        if (deliveryDateHarvestId.delivery_date !== this.deliveryDate) return;
        promises.push(this.$http("get", `harvests/${deliveryDateHarvestId.harvest_id}`));
      });
      const responses = await Promise.all(promises);
      responses.forEach(response => this.flowerHarvests.push(response.result.flower_harvest));

      this.harvestId = null;
      this.flowerCount = 0;
      this.requestFlowerCount = 0;
    },

    async addDraftOrder() {
      const data = this.createRequestBody();
      await this.post("order_items", data);
      await this.$store.dispatch("orders/notifyAddCart");
    },

    async purchace() {
      const data = this.createRequestBody();
      await this.post("order_items/purchace_now", data);
      await this.$store.dispatch("orders/notifyPurchaceComplete");
    },

    async goToConversation() {
      const res = await this.$http("post", "conversations", { farm: { id: this.farm.id } });
      const convarsationId = res.result.conversation.id;
      this.$router.push(`/shop/conversations/${convarsationId}`);
    },

    createRequestBody() {
      const data = {
        order_items: []
      };

      if (this.flowerCount) {
        data.order_items.push(
          {
            count: this.flowerCount,
            delivery_date: this.deliveryDate,
            flower_harvest_id: this.harvest.id,
            standard_id: this.harvest.standard_id,
            order_item_type: null
          }
        );
      }

      if (this.requestFlowerCount) {
        data.order_items.push(
          {
            count: this.requestFlowerCount,
            delivery_date: this.deliveryDate,
            flower_harvest_id: this.harvest.id,
            standard_id: this.harvest.standard_id,
            order_item_type: "request"
          }
        );
      }

      return data;
    },

    async post(url = "/order_items", data) {
      class OrderItemCreateError extends Error {
        constructor(status, message) {
          super(message);
          this.status = status;
          this.name = "OrderItemCreateError";
        }
      }

      try {
        const resOrderItem = await this.$http("post", url, data);
        if (resOrderItem.status === 422) throw new OrderItemCreateError(422, "Failed to adding item into cart");
        if (resOrderItem.status === 521) throw new OrderItemCreateError(521, "Failed to adding item into cart");

        this.deliveryDate = "";
        this.harvestId = null;
      } catch (e) {
        if (e.status === 521) {
          this.$store.dispatch("orders/notifyUnbuyableAreaError");
        } else {
          this.$store.dispatch("orders/notifyAddToCartError");
        }

        console.log(e);
      } finally {
        await this.$store.dispatch("orders/getOrdersDraft");
      }
    }
  },

  computed: {
    mobile() {
      return this.$vuetify.breakpoint.xs;
    },
    productImage() {
      return this.flower.images;
    },
    harvest() {
      return this.flowerHarvests.filter(x => x.id === this.harvestId)[0];
    }
  }
};
</script>

<style lang="scss" scoped>
.breadcrumb-list {
  color: #777;
  font-size: 12px;
  margin-bottom: 16px;

  .link {
    color: #555;
    font-weight: bold;
  }

  .arrow {
    margin: 0 10px 0 10px;
  }
}

.product-image {
  width: 300px;
  margin-right: 16px;

  @media screen and (max-width: 960px) {
    width: auto;
    margin-right: 0;
    margin-bottom: 10px;
  }
}

.order-form {
  margin-top: 16px;
  background-color: #eee;
  padding: 24px;
  color: #555;
}

.cart {
  margin-top: 16px;
}
</style>
